import { useState, useEffect, useMemo } from "react";
import { useApp } from "../context/AppContext";

export default function CannibalizationView() {
  const { proj, activeId, getStore, setStore, dataRef, projects, setProjects, apiFetch,
    saveClusters, saveContent, saveStructure, saveSemClusters, saveLinks,
    flash, setView, view, dark, setDark, t, card, inp, btnP, btnG, btnSm, stitle, muted, th, td,
    userRole, currentUser, loadProjectData, openP, infoId, setInfoId, setActiveId } = useApp();
  if (!proj) return null;

  const [gscRows, setGscRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedQueries, setExpandedQueries] = useState({});
  const [sortBy, setSortBy] = useState("impressions");
  const [searchQ, setSearchQ] = useState("");
  const [minPages, setMinPages] = useState(2);
  const [canPage, setCanPage] = useState(0);
  const CAN_PER_PAGE = 30;
  const [aiResolving, setAiResolving] = useState(null); // query being resolved
  const [aiSolutions, setAiSolutions] = useState({}); // query → solution
  const token = localStorage.getItem("metago_token");

  const resolveWithAI = async (item) => {
    setAiResolving(item.query);
    try {
      const r = await fetch(`/api/projects/${activeId}/cannibalization/resolve`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ query: item.query, pages: item.pages }),
      });
      const d = await r.json();
      if (d.solution) {
        setAiSolutions(prev => ({ ...prev, [item.query]: d.solution }));
      } else if (d.error) {
        flash("Помилка: " + d.error);
      }
    } catch (e) { flash("Помилка: " + e.message); }
    setAiResolving(null);
  };

  useEffect(() => {
    if (!activeId) return;
    setLoading(true);
    apiFetch(`/api/gsc/saved/${activeId}`)
      .then(r => r.ok ? r.json() : { found: false })
      .then(d => {
        const rows = d?.found && Array.isArray(d.rows) ? d.rows : (Array.isArray(d) ? d : []);
        setGscRows(rows);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [activeId, apiFetch]);

  // Find cannibalized queries: same query → multiple pages
  const cannibalized = useMemo(() => {
    if (!gscRows.length) return [];
    const byQuery = {};
    gscRows.forEach(r => {
      const q = (r.query || "").toLowerCase();
      if (!q) return;
      if (!byQuery[q]) byQuery[q] = { query: r.query, pages: [] };
      byQuery[q].pages.push({
        url: r.page || "",
        clicks: r.clicks || 0,
        impressions: r.impressions || 0,
        ctr: r.ctr || 0,
        position: r.position || 0,
      });
    });

    let results = Object.values(byQuery)
      .filter(g => g.pages.length >= minPages)
      .map(g => {
        const pages = g.pages.sort((a, b) => a.position - b.position);
        const totalImpr = pages.reduce((s, p) => s + p.impressions, 0);
        const totalClicks = pages.reduce((s, p) => s + p.clicks, 0);
        const bestPage = pages[0];
        const worstPage = pages[pages.length - 1];
        const posDiff = worstPage.position - bestPage.position;

        // Severity: high if pages are close in position (fighting), low if spread
        let severity = "low";
        if (pages.length >= 3) severity = "high";
        else if (posDiff < 5) severity = "high";
        else if (posDiff < 15) severity = "medium";

        return {
          query: g.query,
          pages,
          totalImpr,
          totalClicks,
          pageCount: pages.length,
          bestPos: bestPage.position,
          severity,
        };
      });

    if (searchQ) {
      const sq = searchQ.toLowerCase();
      results = results.filter(r => r.query.toLowerCase().includes(sq) || r.pages.some(p => p.url.toLowerCase().includes(sq)));
    }

    if (sortBy === "impressions") results.sort((a, b) => b.totalImpr - a.totalImpr);
    else if (sortBy === "pages") results.sort((a, b) => b.pageCount - a.pageCount);
    else if (sortBy === "severity") {
      const sev = { high: 3, medium: 2, low: 1 };
      results.sort((a, b) => sev[b.severity] - sev[a.severity] || b.totalImpr - a.totalImpr);
    }
    else results.sort((a, b) => a.bestPos - b.bestPos);

    return results;
  }, [gscRows, minPages, searchQ, sortBy]);

  const totalPages = Math.ceil(cannibalized.length / CAN_PER_PAGE);
  const visible = cannibalized.slice(canPage * CAN_PER_PAGE, (canPage + 1) * CAN_PER_PAGE);
  const totalConflicts = cannibalized.length;
  const highCount = cannibalized.filter(c => c.severity === "high").length;
  const affectedUrls = new Set(cannibalized.flatMap(c => c.pages.map(p => p.url))).size;

  const sevBadge = (sev) => {
    const s = { high: { bg: "#fee2e2", color: "#991b1b", label: "Високий" }, medium: { bg: "#fef3c7", color: "#92400e", label: "Середній" }, low: { bg: "#dcfce7", color: "#166534", label: "Низький" } };
    const st = s[sev] || s.low;
    return <span style={{ background: st.bg, color: st.color, padding: "2px 8px", borderRadius: 8, fontSize: 10, fontWeight: 600 }}>{st.label}</span>;
  };

  if (!loading && gscRows.length === 0) {
    return (
      <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Канібалізація запитів</h2>
        <div style={{ ...card, textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>Підключіть Google Search Console</p>
          <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 16 }}>Для аналізу канібалізації потрібні дані GSC</p>
          <button style={btnP} onClick={() => setView("gsc")}>Підключити GSC</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Канібалізація запитів</h2>
        <div style={{ ...card, textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 14, color: t.textMuted, animation: "pulse 1.5s infinite" }}>Завантаження даних...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Канібалізація запитів</h2>

      {/* Summary */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
        <div style={{ ...card, marginBottom: 0, textAlign: "center" }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#ef4444" }}>{totalConflicts}</div>
          <div style={{ fontSize: 11, color: t.textMuted }}>конфліктів</div>
        </div>
        <div style={{ ...card, marginBottom: 0, textAlign: "center" }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#f59e0b" }}>{highCount}</div>
          <div style={{ fontSize: 11, color: t.textMuted }}>критичних</div>
        </div>
        <div style={{ ...card, marginBottom: 0, textAlign: "center" }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#8b5cf6" }}>{affectedUrls}</div>
          <div style={{ fontSize: 11, color: t.textMuted }}>сторінок задіяно</div>
        </div>
      </div>

      {/* Info */}
      <div style={{ ...card, padding: "10px 14px", fontSize: 12, color: t.textMuted, lineHeight: 1.5, background: dark ? "rgba(99,102,241,.08)" : "rgba(99,102,241,.04)" }}>
        Канібалізація — це коли кілька сторінок вашого сайту конкурують за один і той самий пошуковий запит. Google не знає яку показувати → позиції падають для обох. <strong>Рішення:</strong> об'єднати контент, додати canonical, або noindex на слабшу сторінку.
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}>
        <input
          type="text" placeholder="Пошук запиту або URL..."
          value={searchQ} onChange={e => { setSearchQ(e.target.value); setCanPage(0); }}
          style={{ flex: 1, minWidth: 200, padding: "6px 10px", fontSize: 12, borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgCard, color: t.text, outline: "none" }}
        />
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...btnG, padding: "5px 10px", fontSize: 11 }}>
          <option value="impressions">За показами</option>
          <option value="severity">За критичністю</option>
          <option value="pages">За кількістю URL</option>
          <option value="position">За позицією</option>
        </select>
        <label style={{ fontSize: 11, color: t.textMuted, display: "flex", alignItems: "center", gap: 4 }}>
          Мін. сторінок:
          <input type="number" min={2} max={10} value={minPages} onChange={e => { setMinPages(Math.max(2, +e.target.value || 2)); setCanPage(0); }}
            style={{ width: 45, padding: "4px 6px", fontSize: 11, borderRadius: 6, border: `1px solid ${t.border}`, background: t.bgCard, color: t.text, textAlign: "center" }} />
        </label>
      </div>

      {/* No results */}
      {visible.length === 0 && (
        <div style={{ ...card, textAlign: "center", padding: 30, color: t.textMuted }}>
          {totalConflicts === 0 ? "Канібалізація не виявлена — все чисто!" : "Нічого не знайдено за фільтрами."}
        </div>
      )}

      {/* Results */}
      {visible.map(item => {
        const isExp = expandedQueries[item.query];
        return (
          <div key={item.query} style={{ ...card, padding: 0, marginBottom: 8 }}>
            <div
              onClick={() => setExpandedQueries(prev => ({ ...prev, [item.query]: !prev[item.query] }))}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", cursor: "pointer", borderBottom: isExp ? `1px solid ${t.border}` : "none" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 12, color: t.textMuted, flexShrink: 0 }}>{isExp ? "▼" : "▶"}</span>
                {sevBadge(item.severity)}
                <span style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>"{item.query}"</span>
                <span style={{ fontSize: 10, color: t.textMuted, flexShrink: 0 }}>{item.pageCount} URL</span>
              </div>
              <div style={{ display: "flex", gap: 12, fontSize: 11, color: t.textMuted, flexShrink: 0 }}>
                <span>{item.totalImpr.toLocaleString()} показів</span>
                <span>{item.totalClicks} кліків</span>
              </div>
            </div>

            {isExp && (
              <div>
                {item.pages.map((p, i) => {
                  const shortUrl = p.url.replace(/^https?:\/\/[^/]+/, "");
                  const isBest = i === 0;
                  return (
                    <div key={p.url} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px 8px 36px", borderBottom: `1px solid ${t.border}`, background: isBest ? (dark ? "rgba(34,197,94,.06)" : "rgba(34,197,94,.04)") : "transparent" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: isBest ? "#16a34a" : "#ef4444", width: 16, flexShrink: 0 }}>
                        {isBest ? "★" : `#${i + 1}`}
                      </span>
                      <span style={{ fontSize: 12, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.url}>
                        {shortUrl}
                      </span>
                      <span style={{ fontSize: 11, color: t.textMuted, width: 55, textAlign: "right" }}>Поз: {p.position}</span>
                      <span style={{ fontSize: 11, color: t.textMuted, width: 50, textAlign: "right" }}>{p.clicks} кл.</span>
                      <span style={{ fontSize: 11, color: t.textMuted, width: 60, textAlign: "right" }}>{p.impressions} пок.</span>
                      <span style={{ fontSize: 11, color: t.textMuted, width: 50, textAlign: "right" }}>{p.ctr}%</span>
                    </div>
                  );
                })}
                {/* AI Solution */}
                {aiSolutions[item.query] ? (
                  <div style={{ padding: "12px 14px 12px 36px" }}>
                    {(() => {
                      const sol = aiSolutions[item.query];
                      const typeColors = {
                        merge: { bg: "rgba(239,68,68,0.08)", border: "rgba(239,68,68,0.15)", icon: "🔀", label: "Об'єднати", color: "#ef4444" },
                        split_intent: { bg: "rgba(99,102,241,0.08)", border: "rgba(99,102,241,0.15)", icon: "✂️", label: "Розділити інтент", color: "#6366f1" },
                        canonical: { bg: "rgba(245,158,11,0.08)", border: "rgba(245,158,11,0.15)", icon: "🔗", label: "Canonical", color: "#f59e0b" },
                        noindex: { bg: "rgba(168,85,247,0.08)", border: "rgba(168,85,247,0.15)", icon: "🚫", label: "Noindex", color: "#a855f7" },
                        monitor: { bg: "rgba(16,185,129,0.08)", border: "rgba(16,185,129,0.15)", icon: "👁", label: "Моніторинг", color: "#10b981" },
                      };
                      const tc = typeColors[sol.type] || typeColors.monitor;
                      return (
                        <div style={{ background: dark ? tc.bg : tc.bg, borderRadius: 12, padding: "14px 16px", border: `1px solid ${dark ? tc.border : tc.border}` }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                            <span style={{ fontSize: 18 }}>{tc.icon}</span>
                            <span style={{ fontSize: 13, fontWeight: 700, color: tc.color }}>{tc.label}</span>
                            <span style={{ fontSize: 11, color: t.textMuted, marginLeft: "auto" }}>AI рішення</span>
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: t.text }}>{sol.summary}</div>
                          {sol.actions && sol.actions.length > 0 && (
                            <div style={{ marginBottom: 8 }}>
                              {sol.actions.map((a, i) => (
                                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "4px 0" }}>
                                  <span style={{ fontSize: 10, fontWeight: 700, color: tc.color, marginTop: 2 }}>{i + 1}.</span>
                                  <span style={{ fontSize: 12, color: t.text }}>{a}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {sol.keepUrl && (
                            <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4 }}>
                              Основна сторінка: <strong style={{ color: "#10b981" }}>{sol.keepUrl.replace(/^https?:\/\/[^/]+/, "")}</strong>
                            </div>
                          )}
                          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                            <button onClick={() => {
                              const text = `Канібалізація: "${item.query}"\nРішення: ${tc.label}\n${sol.summary}\n\nДії:\n${(sol.actions || []).map((a, i) => `${i+1}. ${a}`).join("\n")}`;
                              navigator.clipboard.writeText(text).then(() => flash("Скопійовано"));
                            }} style={{ ...btnSm, fontSize: 10 }}>📋 Копіювати</button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div style={{ padding: "8px 14px 10px 36px", display: "flex", alignItems: "center", gap: 10 }}>
                    <button
                      onClick={() => resolveWithAI(item)}
                      disabled={aiResolving === item.query}
                      style={{ ...btnP, fontSize: 11, padding: "6px 14px", opacity: aiResolving === item.query ? 0.6 : 1 }}
                    >
                      {aiResolving === item.query ? "Аналізую..." : "AI Рішення"}
                    </button>
                    <span style={{ fontSize: 11, color: t.textMuted }}>
                      {item.severity === "high" ? "Критичний конфлікт — рекомендуємо вирішити" : item.severity === "medium" ? "Середній пріоритет" : "Низький пріоритет"}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 4, marginTop: 12 }}>
          <button style={{ ...btnSm, opacity: canPage === 0 ? 0.4 : 1 }} disabled={canPage === 0} onClick={() => setCanPage(p => p - 1)}>←</button>
          <span style={{ fontSize: 12, color: t.textMuted, lineHeight: "28px" }}>{canPage + 1} / {totalPages}</span>
          <button style={{ ...btnSm, opacity: canPage >= totalPages - 1 ? 0.4 : 1 }} disabled={canPage >= totalPages - 1} onClick={() => setCanPage(p => p + 1)}>→</button>
        </div>
      )}
    </div>
  );
};


// ═══ LINK MONITOR VIEW ═══
